import React, { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { api } from "@/lib/http";
import { useAuth } from "@/context/AuthContext";
import { fmtCurrency } from "@/lib/calc";
import { fmtDate } from "@/lib/utils";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { TrendCharts } from "@/components/booking/TrendCharts";
import StaffDashboard from "@/pages/booking/StaffDashboard";
import { ClientSourcesCard } from "@/components/booking/ClientSourcesCard";
import { PayoutsCard } from "@/components/booking/PayoutsCard";
import { MarketingCard } from "@/components/booking/MarketingCard";
import { CapacityMonth, RetentionRebook } from "@/components/booking/PerformanceCharts";
import { CalendarDays, DollarSign, UserPlus, TrendingUp, ArrowRight, CalendarClock, BarChart3, Wallet, ChevronLeft, ChevronRight, CreditCard, User as UserIcon, StickyNote, ClipboardList, Check, HelpCircle, ArrowUp, ArrowDown } from "lucide-react";
import { ACCENTS, BRAND } from "@/lib/palette";

const dsToday = () => { const d = new Date(); const p = (n) => String(n).padStart(2, "0"); return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`; };
const dsTime = (iso) => new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
const statusTone = { booked: "bg-sky-100 text-sky-700", confirmed: "bg-sky-100 text-sky-700", arrived: "bg-amber-100 text-amber-700", checked_out: "bg-emerald-100 text-emerald-700", no_show: "bg-red-100 text-red-600", cancelled: "bg-slate-200 text-slate-500" };

const DaySheet = ({ cur }) => {
  const [rows, setRows] = useState([]);
  const [noteFor, setNoteFor] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [saving, setSaving] = useState(false);
  const nav = useNavigate();
  const today = dsToday();
  const load = () => api.get("/bookings", { params: { start: `${today}T00:00:00+00:00`, end: `${today}T23:59:59+00:00` } })
    .then(({ data }) => setRows((data || []).filter((b) => !b.is_block).sort((a, b) => a.start.localeCompare(b.start)))).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const openNote = (b) => { setNoteFor(b.id); setNoteText(b.notes || ""); };
  const saveNote = async (b) => { setSaving(true); try { await api.patch(`/bookings/${b.id}/notes`, { notes: noteText }); setNoteFor(null); load(); toast.success("Notes saved"); } catch { toast.error("Couldn't save notes"); } finally { setSaving(false); } };
  const key = (b) => b.group_id || b.id;
  return (
    <Card className="rounded-2xl border bg-card p-5 shadow-sm mt-6" data-testid="day-sheet">
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <h3 className="font-heading font-semibold flex items-center gap-2"><ClipboardList className="h-4 w-4 text-primary" />Today's day sheet</h3>
        <span className="text-xs text-muted-foreground">{fmtDate(today)} · {rows.length} appointment{rows.length === 1 ? "" : "s"}</span>
      </div>
      {rows.length === 0 && <div className="text-sm text-muted-foreground py-6 text-center">No appointments today.</div>}
      <div className="space-y-2">
        {rows.map((b) => (
          <div key={b.id} className="rounded-xl border border-border p-3" data-testid={`day-sheet-row-${b.id}`}>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="w-16 shrink-0 font-heading font-bold tabular-nums text-sm">{dsTime(b.start)}</div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{b.client_name || "Walk-in"}</div>
                <div className="text-xs text-muted-foreground truncate">{b.service_name}</div>
              </div>
              <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${statusTone[b.status] || "bg-slate-100 text-slate-600"}`}>{(b.status || "").replace("_", " ")}</span>
              <span className="tabular-nums text-sm font-medium w-16 text-right">{fmtCurrency(b.total_paid ?? b.price, cur, 0)}</span>
            </div>
            <div className="flex items-center gap-1.5 mt-2.5 flex-wrap">
              {!b.paid && b.status !== "checked_out" && b.status !== "cancelled" && (
                <Button size="sm" className="rounded-lg h-8" onClick={() => nav(`/calendar?date=${today}&checkout=${key(b)}`)} data-testid={`ds-pay-${b.id}`}><CreditCard className="h-3.5 w-3.5 mr-1" />Pay</Button>
              )}
              <Button size="sm" variant="outline" className="rounded-lg h-8" onClick={() => nav(`/calendar?date=${today}&open=${key(b)}`)} data-testid={`ds-open-${b.id}`}><CalendarDays className="h-3.5 w-3.5 mr-1" />Calendar</Button>
              {b.client_id && <Button size="sm" variant="outline" className="rounded-lg h-8" onClick={() => nav(`/clients?client=${b.client_id}`)} data-testid={`ds-client-${b.id}`}><UserIcon className="h-3.5 w-3.5 mr-1" />Client</Button>}
              <Button size="sm" variant="outline" className="rounded-lg h-8" onClick={() => (noteFor === b.id ? setNoteFor(null) : openNote(b))} data-testid={`ds-notes-${b.id}`}><StickyNote className="h-3.5 w-3.5 mr-1" />Notes{b.notes ? " ✓" : ""}</Button>
              {b.client_id && <Button size="sm" variant="outline" className="rounded-lg h-8" onClick={() => nav(`/clients?client=${b.client_id}&tab=charts`)} data-testid={`ds-chart-${b.id}`}><ClipboardList className="h-3.5 w-3.5 mr-1" />Chart</Button>}
            </div>
            {noteFor === b.id && (
              <div className="mt-2.5" data-testid={`ds-note-editor-${b.id}`}>
                <textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} rows={2} placeholder="Appointment notes…" className="w-full rounded-lg border border-input bg-background p-2.5 text-sm" data-testid={`ds-note-input-${b.id}`} />
                <div className="flex gap-2 mt-1.5">
                  <Button size="sm" className="rounded-lg h-8" disabled={saving} onClick={() => saveNote(b)} data-testid={`ds-note-save-${b.id}`}><Check className="h-3.5 w-3.5 mr-1" />Save notes</Button>
                  <Button size="sm" variant="ghost" className="rounded-lg h-8" onClick={() => setNoteFor(null)}>Cancel</Button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
};

const Stat = ({ icon: Icon, label, value, accent, testId, delta, deltaKind, cur, hint, sub }) => {
  const showDelta = typeof delta === "number" && isFinite(delta);
  const up = delta > 0;
  const deltaText = deltaKind === "currency"
    ? fmtCurrency(Math.abs(delta), cur, 0)
    : `${Math.abs(delta).toFixed(1)} pts`;
  return (
    <Card className="rounded-2xl border bg-card p-5 shadow-sm">
      <div className="flex items-center gap-2 text-muted-foreground mb-2">
        <Icon className="h-4 w-4" style={{ color: accent }} />
        <span className="font-mono-label text-[11px] uppercase tracking-wider">{label}</span>
        {hint && (
          <span title={hint} className="cursor-help ml-auto" data-testid={`${testId}-info`}>
            <HelpCircle className="h-3.5 w-3.5 opacity-50 hover:opacity-100" />
          </span>
        )}
      </div>
      <div className="flex items-end gap-2 flex-wrap">
        <div className="font-heading font-bold text-2xl sm:text-3xl tabular-nums" data-testid={testId}>{value}</div>
        {showDelta && delta !== 0 && (
          <span className={`mb-1 inline-flex items-center gap-0.5 text-xs font-semibold ${up ? "text-emerald-600" : "text-red-500"}`} data-testid={`${testId}-delta`}>
            {up ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}{deltaText}
          </span>
        )}
      </div>
      {showDelta && <div className="text-[11px] text-muted-foreground mt-0.5">{delta === 0 ? "same as" : "vs"} {sub}</div>}
      {!showDelta && sub && <div className="text-[11px] text-muted-foreground mt-0.5" data-testid={`${testId}-sub`}>{sub}</div>}
    </Card>
  );
};

export default function Dashboard({ embedded }) {
  const { business, isAdmin, isReception, isStaff, can } = useAuth();
  const cur = business?.currency || "AUD";
  const [stats, setStats] = useState(null);
  const [insights, setInsights] = useState(null);
  const [trends, setTrends] = useState(null);
  const [mult, setMult] = useState(4);
  const nowMonth = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; };
  const [month, setMonth] = useState(nowMonth());
  const isCurrentMonth = month === nowMonth();
  const shiftMonth = (delta) => {
    const [y, m] = month.split("-").map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  };
  const monthLabel = (() => {
    const [y, m] = month.split("-").map(Number);
    return new Date(y, m - 1, 1).toLocaleDateString("en-AU", { month: "long", year: "numeric" });
  })();
  const prevMonthLabel = (() => {
    if (!stats?.prev_month) return "last month";
    const [y, m] = stats.prev_month.split("-").map(Number);
    return new Date(y, m - 1, 1).toLocaleDateString("en-AU", { month: "long" });
  })();
  const prevSub = stats?.prev_is_baseline ? "your history" : (stats?.prev_through_day ? `${prevMonthLabel} 1–${stats.prev_through_day} (same point)` : prevMonthLabel);

  useEffect(() => {
    if (!isAdmin) return;
    api.get(`/dashboard/stats?month=${month}`).then(({ data }) => { setStats(data); setMult(data.kpi_multiplier || 4); }).catch(() => {});
  }, [isAdmin, month]);

  useEffect(() => {
    if (!isAdmin || !isCurrentMonth) { setInsights(null); return; }
    api.get("/dashboard/insights").then(({ data }) => setInsights(data)).catch(() => {});
    api.get("/dashboard/trends").then(({ data }) => setTrends(data)).catch(() => {});
  }, [isAdmin, isCurrentMonth]);

  // reception has no dashboard — send them to the calendar
  if (isReception) return <Navigate to="/calendar" replace />;
  // staff see only their own performance
  if (isStaff) return <StaffDashboard />;

  const saveMult = async (v) => {
    setMult(v);
    if (isAdmin) { try { await api.put("/business", { kpi_multiplier: v }); const { data } = await api.get(`/dashboard/stats?month=${month}`); setStats(data); } catch (e) { console.error("Failed to update KPI multiplier", e); } }
  };

  return (
    <div className={embedded ? "" : "max-w-6xl mx-auto px-4 sm:px-6 py-8"}>
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <div>
          {embedded ? <h2 className="font-heading font-semibold text-base">The numbers</h2> : <><h1 className="font-heading font-extrabold text-2xl sm:text-3xl tracking-tight">Dashboard</h1><p className="text-muted-foreground mt-1">{business?.name}</p></>}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1 rounded-lg border bg-card px-1 py-1" data-testid="dashboard-month-nav">
            <Button variant="ghost" size="icon" className="h-8 w-8 rounded-md" onClick={() => shiftMonth(-1)} data-testid="dashboard-month-prev"><ChevronLeft className="h-4 w-4" /></Button>
            <span className="min-w-[8rem] text-center text-sm font-semibold tabular-nums" data-testid="dashboard-month-label">{monthLabel}</span>
            <Button variant="ghost" size="icon" className="h-8 w-8 rounded-md" onClick={() => shiftMonth(1)} data-testid="dashboard-month-next"><ChevronRight className="h-4 w-4" /></Button>
            {!isCurrentMonth && <Button variant="ghost" size="sm" className="h-8 rounded-md text-xs" onClick={() => setMonth(nowMonth())} data-testid="dashboard-month-today">This month</Button>}
          </div>
          <Link to="/calendar" className="hidden sm:block"><Button className="rounded-lg" data-testid="dashboard-open-calendar">Open calendar <ArrowRight className="h-4 w-4 ml-1.5" /></Button></Link>
        </div>
      </div>

      {!stats ? (
        <div className="text-muted-foreground py-16 text-center">Loading…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Stat icon={TrendingUp} label="Sales (Month)" accent={ACCENTS.three} value={fmtCurrency(stats.month_sales, cur, 0)} testId="stat-month-revenue" sub={isCurrentMonth && stats.forecast_month > stats.month_sales ? `On track for ${fmtCurrency(stats.forecast_month, cur, 0)}` : undefined} hint="Checked-out appointments — money taken. Excludes credit, vouchers and package sessions." />
            <Stat icon={CalendarClock} label="Booked Value (Month)" accent={ACCENTS.four} value={fmtCurrency(stats.booked_upcoming, cur, 0)} testId="stat-booked-upcoming" hint="Total value of every appointment booked this month, including ones already paid. Cancellations excluded." />
            <Stat icon={CalendarDays} label="Bookings (Month)" accent={ACCENTS.five} value={stats.month_bookings} testId="stat-month-bookings" sub={isCurrentMonth ? `${stats.today_bookings} today` : undefined} />
            <Stat icon={UserPlus} label="New Clients (Month)" accent={ACCENTS.six} value={stats.new_clients_month} testId="stat-new-clients" sub={isCurrentMonth ? `${fmtCurrency(stats.today_sales, cur, 0)} taken today` : undefined} />
          </div>

          <div className="grid lg:grid-cols-5 gap-4 mb-6" data-testid="dashboard-performance">
            <div className="lg:col-span-3">{isCurrentMonth ? (trends ? <CapacityMonth cap={trends.capacity} monthLabel={monthLabel.split(" ")[0]} /> : <div className="h-56 rounded-2xl bg-muted/40 animate-pulse" />) : <Card className="rounded-2xl p-4 text-sm text-muted-foreground flex items-center gap-2"><CalendarClock className="h-4 w-4" />Booked capacity is shown for the current month — use "This month" to see it.</Card>}</div>
            <div className="lg:col-span-2"><RetentionRebook months={trends?.months} stats={stats} prevSub={prevSub} /></div>
          </div>

          {insights && (
            <Card className="rounded-2xl p-4 mb-6" data-testid="dashboard-action-items">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono-label text-[11px] uppercase tracking-wider text-muted-foreground mr-1">To do</span>
                {insights.action_items.filter((a) => a.count > 0).map((a) => (
                  <Link key={a.key} to={a.link} className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1 text-xs hover:bg-muted transition-colors" data-testid={`action-${a.key}`}>{a.label}<span className="font-bold tabular-nums text-primary">{a.count}</span></Link>
                ))}
                {insights.action_items.every((a) => !a.count) && <span className="text-xs text-muted-foreground">Nothing outstanding — nice.</span>}
                <span className="ml-auto text-xs text-muted-foreground" data-testid="dashboard-capacity">Team this week <b className="text-foreground tabular-nums">{insights.clinic_utilisation}%</b> booked</span>
              </div>
            </Card>
          )}

          <div className="grid lg:grid-cols-2 gap-4 mb-6 items-start"><PayoutsCard cur={cur} /><MarketingCard cur={cur} /></div>

          {can("view_reports") && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6" data-testid="dashboard-report-shortcuts">
              {[
                { key: "executive", label: "Executive summary", desc: "Appointments, sales & rebooking at a glance", icon: BarChart3, accent: ACCENTS.one },
                { key: "sales", label: "Sales report", desc: "Takings by day, staff & payment method", icon: DollarSign, accent: ACCENTS.two },
                { key: "future_value", label: "Future value", desc: "Every appointment in the period — taken + still to come (matches Booked Value)", icon: Wallet, accent: ACCENTS.four },
              ].map((r) => (
                <Link key={r.key} to={`/reports?open=${r.key}`} data-testid={`dashboard-report-${r.key}`}>
                  <Card className="rounded-2xl border bg-card p-4 shadow-sm hover:border-primary/60 hover:shadow transition-all h-full group">
                    <div className="flex items-center gap-2 mb-1"><r.icon className="h-4 w-4" style={{ color: r.accent }} /><span className="font-heading font-semibold text-sm">{r.label}</span><ArrowRight className="h-3.5 w-3.5 text-muted-foreground ml-auto group-hover:text-primary" /></div>
                    <p className="text-xs text-muted-foreground">{r.desc}</p>
                  </Card>
                </Link>
              ))}
            </div>
          )}

          <div className="grid lg:grid-cols-2 gap-6">
            <Card className="rounded-2xl border bg-card p-5 shadow-sm" data-testid="staff-kpi">
              <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
                <h3 className="font-heading font-semibold">Staff KPI vs wage target</h3>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">Target = weekly wage ×</span>
                  <Input type="number" min="0" step="0.5" value={mult} onChange={(e) => saveMult(parseFloat(e.target.value) || 0)} className="h-8 w-16 rounded-lg" data-testid="kpi-multiplier" disabled={!isAdmin} />
                </div>
              </div>
              <div className="space-y-3">
                {stats.staff_kpi.map((s) => {
                  const target = s.target || 0;
                  const pct = target > 0 ? Math.min((s.sales / target) * 100, 100) : 0;
                  return (
                    <div key={s.id} data-testid={`kpi-row-${s.id}`}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2.5">
                          <span className="h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{ backgroundColor: s.color || BRAND.primary }}>
                            {s.name.split(" ").map((w) => w[0]).join("").slice(0, 2)}
                          </span>
                          <div>
                            <div className="font-medium text-sm">{s.name}</div>
                            <div className="text-xs text-muted-foreground">{s.bookings} bookings · target MTD {fmtCurrency(target, cur, 0)}</div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-heading font-bold tabular-nums">{fmtCurrency(s.sales, cur, 0)}</div>
                          <div className={`text-xs font-medium ${s.sales >= target ? "text-primary" : "text-muted-foreground"}`}>{Math.round(pct)}%</div>
                        </div>
                      </div>
                      <div className="h-2 rounded-full bg-muted overflow-hidden"><div className="h-full" style={{ width: `${pct}%`, backgroundColor: s.sales >= target ? BRAND.success : (s.color || BRAND.primary) }} /></div>
                    </div>
                  );
                })}
                {stats.staff_kpi.length === 0 && <div className="text-sm text-muted-foreground">No staff yet.</div>}
              </div>
            </Card>

            <ClientSourcesCard />
          </div>

          <Card className="rounded-2xl border bg-card p-5 shadow-sm mt-6" data-testid="discounts-report">
            <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
              <h3 className="font-heading font-semibold">Discounts given</h3>
              <div className="text-right">
                <div className="font-mono-label text-[11px] uppercase tracking-wider text-muted-foreground">{monthLabel}</div>
                <div className="font-heading font-bold text-xl tabular-nums text-secondary" data-testid="discounts-month-total">{fmtCurrency(stats.discounts_month_total || 0, cur, 2)}</div>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <div className="font-mono-label text-[11px] uppercase tracking-wider text-muted-foreground mb-2">By staff (this month)</div>
                <div className="space-y-2">
                  {(stats.discounts_by_staff || []).map((s) => (
                    <div key={s.id} className="flex justify-between text-sm" data-testid={`discount-staff-${s.id}`}>
                      <span>{s.name} <span className="text-muted-foreground">· {s.count}</span></span>
                      <span className="tabular-nums font-medium">{fmtCurrency(s.total, cur, 2)}</span>
                    </div>
                  ))}
                  {(!stats.discounts_by_staff || stats.discounts_by_staff.length === 0) && <div className="text-sm text-muted-foreground">No discounts this month.</div>}
                </div>
              </div>
              <div>
                <div className="font-mono-label text-[11px] uppercase tracking-wider text-muted-foreground mb-2">By month</div>
                <div className="space-y-2">
                  {(stats.discounts_by_month || []).map((m) => (
                    <div key={m.month} className="flex justify-between text-sm" data-testid={`discount-month-${m.month}`}>
                      <span>{m.month} <span className="text-muted-foreground">· {m.count}</span></span>
                      <span className="tabular-nums font-medium">{fmtCurrency(m.total, cur, 2)}</span>
                    </div>
                  ))}
                  {(!stats.discounts_by_month || stats.discounts_by_month.length === 0) && <div className="text-sm text-muted-foreground">No discounts yet.</div>}
                </div>
              </div>
            </div>
          </Card>
          {isCurrentMonth && <div className="mt-6" data-testid="dashboard-trends"><TrendCharts money={(v) => fmtCurrency(v || 0, cur, 0)} data={trends} /></div>}
        </>
      )}
      <DaySheet cur={cur} />
    </div>
  );
}
